<template>
  <div class="card card-custom">
    <div class="card-body container-fluid py-8 px-8 py-lg-10 px-lg-10">

      <WorkQueueChrome
        :active-lane="activeLane"
        :subtitle="laneSubtitle"
        :lane-counts="laneCounts"
        @lane="onLane"
        @search="onChromeSearch"
      />

      <b-alert v-if="errorAlert" variant="warning" show>
        <div class="text-dark" v-html="errorAlert"></div>
      </b-alert>

      <!-- ========== POSTING LANE ========== -->
      <div v-if="activeLane === 'posting'">
        <div v-if="postingLoaded">
          <StatusSummaryBar
            :status-counts="statusCounts"
            :active-status="activeStatus"
            @filter="onFilter"
          />

          <div class="d-flex align-items-center justify-content-end mb-4">
            <b-form-checkbox v-model="migrationOrder" switch @change="onToggleMigrationOrder">
              <span class="font-weight-bold text-dark">Migration order &mdash; cash first</span>
            </b-form-checkbox>
          </div>

          <div v-if="!migrationOrder && activeStatus" class="d-flex align-items-center mb-4">
            <span class="text-muted font-size-sm mr-3">
              Showing <span class="font-weight-bold text-dark">{{ activeStatus }}</span> only
            </span>
            <button class="btn btn-sm btn-light-primary font-weight-bold text-uppercase" @click="onFilter(null)">
              Clear filter
            </button>
          </div>

          <QueueSection
            v-for="section in (migrationOrder ? migrationSections : [])"
            :key="'migration-' + section.status"
            :status="section.status"
            :items="section.items"
            :variant="section.variant"
            @action="onAction"
          />

          <QueueSection
            v-for="section in (migrationOrder ? [] : visibleSections)"
            :key="section.status"
            :status="section.status"
            :items="section.items"
            :variant="section.variant"
            @action="onAction"
          />

          <div v-if="isEmpty" class="text-center text-muted py-10">
            <div class="font-size-h4 font-weight-bold">Nothing in the queue</div>
            <div class="font-size-sm">
              {{ migrationOrder ? "No actionable rows to migrate." : "No work items match the current view." }}
            </div>
          </div>
        </div>
      </div>

      <!-- ========== MAINTENANCE LANE ========== -->
      <div v-if="activeLane === 'maintenance'">
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-4">
          <div>
            <div class="font-weight-bold text-dark">Policy Maintenance</div>
            <div class="text-muted font-size-sm" v-if="maintenance.window">
              Fuse MSI · {{ maintenance.window.label }}
            </div>
          </div>
          <div class="d-flex flex-wrap align-items-end">
            <div class="mr-3 mb-2">
              <label class="text-muted font-size-xs mb-1 d-block">Window start</label>
              <input v-model="maintenanceWindowStart" type="date" class="form-control form-control-sm" style="width: 10rem;" />
            </div>
            <div class="mr-3 mb-2">
              <label class="text-muted font-size-xs mb-1 d-block">Window end</label>
              <input v-model="maintenanceWindowEnd" type="date" class="form-control form-control-sm" style="width: 10rem;" />
            </div>
            <button class="btn btn-sm btn-light font-weight-bold text-uppercase mr-2 mb-2" :disabled="maintenanceBusy" @click="loadMaintenance">
              Apply window
            </button>
            <button class="btn btn-sm btn-light-primary font-weight-bold text-uppercase mr-2 mb-2" :disabled="maintenanceBusy" @click="loadBordereauPreview">
              Preview bordereau
            </button>
            <button class="btn btn-sm btn-primary font-weight-bold text-uppercase mb-2" :disabled="maintenanceBusy" @click="exportBordereau">
              Export bordereau
            </button>
          </div>
        </div>

        <div v-if="maintenance.sourceFailed" class="alert alert-danger mb-4">
          {{ maintenance.sourceError || "Unable to load bound policies from CRM. Counts below are not reliable." }}
        </div>

        <div v-if="maintenance.summary" class="d-flex flex-wrap mb-4">
          <span class="label label-inline label-light-primary font-weight-bold mr-2 mb-2">
            Bound {{ maintenance.summary.bound }}
          </span>
          <span class="label label-inline label-light-warning font-weight-bold mr-2 mb-2">
            Needs posting {{ maintenance.summary.needsPosting }}
            <span v-if="maintenance.summary.needsPostingPremium">
              ({{ maintenance.summary.needsPostingPremium | currency("$", ",", 0, ".", "front", true) }})
            </span>
          </span>
          <span class="label label-inline label-light-info font-weight-bold mr-2 mb-2">
            Renewal incomplete {{ maintenance.summary.renewalIncomplete }}
          </span>
          <span v-if="maintenance.summary.cannotDetermine" class="label label-inline label-light-danger font-weight-bold mr-2 mb-2">
            Cannot determine {{ maintenance.summary.cannotDetermine }}
          </span>
          <span v-if="maintenance.summary.premiumMismatch" class="label label-inline label-light-danger font-weight-bold mr-2 mb-2">
            Premium mismatch {{ maintenance.summary.premiumMismatch }}
          </span>
          <span class="label label-inline label-light-success font-weight-bold mr-2 mb-2">
            Done {{ maintenance.summary.done }}
          </span>
          <b-form-checkbox v-model="showDoneMaintenance" class="ml-3" @change="loadMaintenance">
            Show done
          </b-form-checkbox>
        </div>

        <details v-if="maintenance.opsCheck" class="mb-4 text-muted font-size-sm">
          <summary>{{ maintenance.opsCheck.label }}</summary>
          <div class="mt-2">{{ maintenance.opsCheck.note }}</div>
        </details>

        <div v-if="bordereauPreview" class="border rounded p-4 mb-5 bg-light">
          <div class="font-weight-bold mb-2">Bordereau preview</div>
          <div v-if="bordereauPreview.sourceFailed" class="alert alert-danger mb-3">
            {{ bordereauPreview.sourceError || "Source query failed — preview is not reliable." }}
          </div>
          <div class="text-muted font-size-sm mb-3">
            {{ bordereauPreview.control.policies }} posted policies ·
            {{ bordereauPreview.control.lines }} lines ·
            base premium total
            {{ bordereauPreview.control.basePremiumTotal | currency("$", ",", 0, ".", "front", true) }}
          </div>
          <div v-if="(bordereauPreview.exceptions || []).length" class="text-warning font-size-sm mb-2">
            {{ bordereauPreview.exceptions.length }} policies still need attention and are not in the written bordereau.
          </div>
          <div v-if="(bordereauPreview.variances || []).length" class="text-danger font-size-sm mb-2">
            {{ bordereauPreview.variances.length }} stamped-back policies have Zoho vs Insight premium mismatch (Insight figure is on the bordereau).
            <ul class="mb-0 mt-1 pl-4">
              <li v-for="(v, idx) in bordereauPreview.variances.slice(0, 20)" :key="'var-' + idx">
                {{ v.policyNumber }} · {{ v.clientName }} — Zoho {{ v.zohoPremium }} / Insight {{ v.insightPremium }}
              </li>
            </ul>
          </div>
          <div class="table-responsive" style="max-height: 240px; overflow: auto;">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>Policy</th><th>Client</th><th>State</th><th>Category</th><th>Premium</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(line, idx) in bordereauPreview.lines.slice(0, 50)" :key="idx">
                  <td>{{ line.policyNumber }}</td>
                  <td>{{ line.clientName }}</td>
                  <td>{{ line.state }}</td>
                  <td>{{ line.vehicleCategory }}</td>
                  <td>{{ line.basePremium != null ? line.basePremium : "" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <MaintenanceTaskCard
          v-for="task in maintenance.tasks || []"
          :key="task.id"
          :task="task"
          @action="onMaintenanceAction"
        />

        <div v-if="maintenanceLoaded && !(maintenance.tasks || []).length" class="text-center text-muted py-10">
          <div class="font-size-h4 font-weight-bold">Nothing needs attention</div>
          <div class="font-size-sm">No maintenance tasks in this window.</div>
        </div>
      </div>

      <!-- ========== CLIENTS LANE ========== -->
      <div v-if="activeLane === 'clients'">
        <form class="d-flex mb-5" @submit.prevent="runClientSearch">
          <input
            v-model="clientQuery"
            type="search"
            class="form-control form-control-solid mr-2"
            placeholder="Search by name, trading name, business number, or policy number"
          />
          <button type="submit" class="btn btn-primary font-weight-bold text-uppercase">Search</button>
        </form>

        <div v-if="clientSearchBusy" class="text-muted">Searching…</div>
        <div v-else-if="clientResults.length === 0 && clientSearched" class="text-muted">No clients found.</div>

        <div
          v-for="result in clientResults"
          :key="result.id"
          class="d-flex justify-content-between align-items-center border rounded p-4 mb-3 bg-white"
        >
          <div>
            <div class="font-weight-bolder text-dark">{{ result.name }}</div>
            <div class="text-muted font-size-sm">
              <span v-if="result.tradingName">{{ result.tradingName }} · </span>
              <span v-if="result.state">{{ result.state }} · </span>
              <span v-if="result.businessNumber">{{ result.businessNumber }}</span>
            </div>
            <div class="font-size-sm mt-1">
              <span v-if="result.linkedForBilling" class="text-success">Ready for billing</span>
              <span v-else class="text-warning">
                Needs INSIGHT link — open client, then use Link for billing
              </span>
            </div>
          </div>
          <button class="btn btn-sm btn-primary font-weight-bold text-uppercase" @click.prevent="openClient(result.id)">
            {{ result.linkedForBilling ? "Open client" : "Open to process" }}
          </button>
        </div>
      </div>

      <!-- ========== BROKERS LANE ========== -->
      <div v-if="activeLane === 'brokers'">
        <div v-if="brokersLoaded">
          <div class="text-muted mb-4" v-if="brokers.totals">
            {{ brokers.totals.brokers }} brokers · {{ brokers.totals.policies }} policies ·
            {{ brokers.totals.linkedBrokers }} linked
          </div>
          <div
            v-for="broker in brokers.brokers || []"
            :key="broker.id"
            class="border rounded p-4 mb-3 bg-white"
          >
            <div class="font-weight-bolder text-dark">{{ broker.name }}</div>
            <div class="text-muted font-size-sm">
              {{ broker.tradingName || broker.legalEntity || "" }}
              <span v-if="broker.abn"> · {{ broker.abn }}</span>
            </div>
            <div class="font-size-sm mt-1">
              {{ broker.policyCount }} policies · {{ broker.clientCount }} clients
              <span v-if="broker.brokerIdPresent" class="text-success ml-2">Linked</span>
              <span v-else class="text-warning ml-2">Not linked</span>
            </div>
          </div>
          <div v-if="!(brokers.brokers || []).length" class="text-muted text-center py-10">
            No producing brokers to show.
          </div>
        </div>
      </div>

      <b-modal ref="unwiredActionModal" :title="modalTitle" size="md" ok-only ok-title="Close">
        <p class="mb-0">
          The <span class="font-weight-bold">{{ modalActionLabel }}</span> action is wired in a later slice.
        </p>
        <p v-if="modalItem" class="text-muted font-size-sm mt-2 mb-0">
          {{ modalItem.client }} &middot; {{ modalItem.policyNumber }}
        </p>
      </b-modal>

    </div>
  </div>
</template>

<script>
import KTUtil from "@/assets/js/components/util";
import { ADD_BODY_CLASSNAME, REMOVE_BODY_CLASSNAME } from "@/core/services/store/htmlclass.module";
import { getWorkQueue, createInsightClient, stampBack, QUEUE_STATUSES } from "@/services/workbench-service";
import {
  searchClients,
  getPolicyMaintenance,
  previewBordereau,
  getProducingBrokers
} from "@/services/work-queue-service";
import ApiService from "@/core/services/api.service";
import StatusSummaryBar from "./components/StatusSummaryBar.vue";
import QueueSection from "./components/QueueSection.vue";
import WorkQueueChrome from "./components/WorkQueueChrome.vue";
import MaintenanceTaskCard from "./components/MaintenanceTaskCard.vue";

const STATUS_VARIANT = {
  "Ready to Prepare": "primary",
  "Missing Evidence": "warning",
  "Fleet Uploaded Not Processed": "info",
  "Pricing Review": "info",
  "Ready to Post": "success",
  "Posted Awaiting Readback": "primary",
  "Stamped Back": "success",
  "Exception": "danger"
};

const LANES = ["posting", "maintenance", "clients", "brokers"];

export default {
  name: "Workbench",

  components: { StatusSummaryBar, QueueSection, WorkQueueChrome, MaintenanceTaskCard },

  data() {
    return {
      postingLoaded: false,
      errorAlert: null,
      workItems: [],
      activeStatus: null,
      migrationOrder: false,
      modalItem: null,
      modalActionLabel: null,
      maintenanceLoaded: false,
      maintenanceBusy: false,
      maintenance: {},
      maintenanceWindowStart: "2026-05-18",
      maintenanceWindowEnd: "2026-06-30",
      showDoneMaintenance: false,
      bordereauPreview: null,
      clientQuery: "",
      clientResults: [],
      clientSearched: false,
      clientSearchBusy: false,
      brokersLoaded: false,
      brokers: {}
    };
  },

  mounted() {
    this.$store.commit("setWizardTitle", "Underwriting Work Queue");
    this.$store.commit("setWizardSubtitle", this.laneSubtitle);
    this.bootstrapLane();
  },

  watch: {
    "$route.query.lane": function() {
      this.$store.commit("setWizardSubtitle", this.laneSubtitle);
      this.bootstrapLane();
    },
    "$route.query.q": function(q) {
      if (this.activeLane === "clients" && q) {
        this.clientQuery = q;
        this.runClientSearch();
      }
    }
  },

  computed: {
    activeLane() {
      const lane = this.$route.query.lane;
      return LANES.includes(lane) ? lane : "posting";
    },
    laneSubtitle() {
      if (this.activeLane === "maintenance") return "Policy Maintenance";
      if (this.activeLane === "clients") return "Clients";
      if (this.activeLane === "brokers") return "Producing Brokers";
      return "Posting";
    },
    laneCounts() {
      return {
        posting: this.workItems.length || null,
        maintenance: this.maintenance.summary ? this.maintenance.summary.needsPosting + this.maintenance.summary.renewalIncomplete : null,
        clients: null,
        brokers: this.brokers.totals ? this.brokers.totals.brokers : null
      };
    },
    statusCounts() {
      return QUEUE_STATUSES.map(status => ({
        status,
        count: this.workItems.filter(i => i.status === status).length,
        variant: STATUS_VARIANT[status] || "primary"
      }));
    },
    visibleSections() {
      return QUEUE_STATUSES
        .filter(status => !this.activeStatus || status === this.activeStatus)
        .map(status => ({
          status,
          variant: STATUS_VARIANT[status] || "primary",
          items: this.workItems.filter(i => i.status === status)
        }))
        .filter(section => section.items.length > 0);
    },
    migrationSections() {
      const actionable = this.workItems.filter(i => !i.nextAction || i.nextAction.key !== "view");
      const ranked = actionable.filter(i => i.cashRank && i.cashRank.hasMoney);
      const unranked = actionable.filter(i => !(i.cashRank && i.cashRank.hasMoney));
      const sections = [];
      if (ranked.length) {
        sections.push({ status: "Migration order — cash first", variant: "dark", items: ranked });
      }
      if (unranked.length) {
        sections.push({
          status: "No cash rank — premium absent, zero or unparseable",
          variant: "warning",
          items: unranked
        });
      }
      return sections;
    },
    isEmpty() {
      return this.migrationOrder ? this.migrationSections.length === 0 : this.visibleSections.length === 0;
    },
    modalTitle() {
      return this.modalActionLabel || "Action";
    }
  },

  methods: {
    bootstrapLane() {
      if (this.activeLane === "posting") this.loadQueue();
      if (this.activeLane === "maintenance") this.loadMaintenance();
      if (this.activeLane === "clients") {
        if (this.$route.query.q) {
          this.clientQuery = this.$route.query.q;
          this.runClientSearch();
        }
      }
      if (this.activeLane === "brokers") this.loadBrokers();
    },
    onLane(lane) {
      this.clearError();
    },
    onChromeSearch(q) {
      this.clientQuery = q;
      if (this.activeLane === "clients") this.runClientSearch();
    },
    loadQueue() {
      this.clearError();
      this.startPageLoading();
      getWorkQueue({
        demo: this.$route.query.demo === "1",
        order: this.migrationOrder ? "cash-first" : "default"
      })
        .then(items => {
          this.workItems = Array.isArray(items) ? items : [];
          this.postingLoaded = true;
          this.stopPageLoading();
        })
        .catch(() => this.handleError("An unexpected error has occurred while loading the work queue."));
    },
    maintenanceWindowParams() {
      return {
        start: this.maintenanceWindowStart,
        end: this.maintenanceWindowEnd
      };
    },
    loadMaintenance() {
      this.clearError();
      this.startPageLoading();
      this.maintenanceBusy = true;
      getPolicyMaintenance({
        ...this.maintenanceWindowParams(),
        showDone: this.showDoneMaintenance
      })
        .then(result => {
          this.maintenance = result || {};
          this.maintenanceLoaded = true;
          if (result && result.window) {
            if (result.window.start) this.maintenanceWindowStart = result.window.start;
            if (result.window.end) this.maintenanceWindowEnd = result.window.end;
          }
        })
        .catch(() => this.setError("Unable to load policy maintenance right now."))
        .finally(() => {
          this.maintenanceBusy = false;
          this.stopPageLoading();
        });
    },
    loadBordereauPreview() {
      this.maintenanceBusy = true;
      previewBordereau(this.maintenanceWindowParams())
        .then(result => {
          this.bordereauPreview = result;
        })
        .catch(() => this.setError("Unable to preview the bordereau right now."))
        .finally(() => {
          this.maintenanceBusy = false;
        });
    },
    exportBordereau() {
      this.maintenanceBusy = true;
      const qs = new URLSearchParams(this.maintenanceWindowParams()).toString();
      const path = qs ? `/workbench/msi-bdx/export?${qs}` : "/workbench/msi-bdx/export";
      ApiService.get(path, { responseType: "blob" })
        .then(response => {
          const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "msi-bordereau.csv";
          a.click();
          window.URL.revokeObjectURL(url);
        })
        .catch(() => this.setError("Unable to export the bordereau right now."))
        .finally(() => {
          this.maintenanceBusy = false;
        });
    },
    async onMaintenanceAction(task, action) {
      if (!action) return;
      if (action.route) {
        this.$router.push(action.route).catch(() => {});
        return;
      }
      if (action.key === "copy-quote" && task.clientId) {
        this.$router.push(`/workbench/clients/${task.clientId}?tab=policies`).catch(() => {});
      }
    },
    runClientSearch() {
      const q = String(this.clientQuery || "").trim();
      if (q.length < 2) {
        this.setError("Enter at least two characters to search.");
        return;
      }
      this.clearError();
      this.clientSearchBusy = true;
      this.clientSearched = true;
      searchClients(q)
        .then(result => {
          this.clientResults = (result && result.results) || [];
        })
        .catch(() => this.setError("Unable to search clients right now."))
        .finally(() => {
          this.clientSearchBusy = false;
        });
    },
    openClient(clientId) {
      this.$router.push(`/workbench/clients/${clientId}`).catch(() => {});
    },
    loadBrokers() {
      this.startPageLoading();
      getProducingBrokers()
        .then(result => {
          this.brokers = result || {};
          this.brokersLoaded = true;
        })
        .catch(() => this.setError("Unable to load producing brokers right now."))
        .finally(() => this.stopPageLoading());
    },
    onFilter(status) {
      this.activeStatus = status;
    },
    onToggleMigrationOrder(checked) {
      this.migrationOrder = checked;
      this.activeStatus = null;
      this.loadQueue();
    },
    onAction(item) {
      const action = item.nextAction || {};
      if (action.key === "create-insight-client") {
        this.confirmCreateInsightClient(item);
        return;
      }
      if (action.key === "stamp-back") {
        this.confirmStampBack(item);
        return;
      }
      if (action.route) {
        this.$router.push(action.route).catch(() => {});
        return;
      }
      this.modalItem = item;
      this.modalActionLabel = action.label;
      this.$refs.unwiredActionModal.show();
    },
    confirmCreateInsightClient(item) {
      this.clearError();
      this.$bvModal
        .msgBoxConfirm("Create this client in the billing system?", {
          title: "Link client for billing",
          okTitle: "Continue",
          cancelTitle: "Cancel",
          okVariant: "primary",
          centered: true
        })
        .then(confirmed => {
          if (confirmed) this.runCreateInsightClient(item);
        })
        .catch(() => {});
    },
    runCreateInsightClient(item) {
      if (!item.clientId) {
        this.setError("This work item has no linked client record, so billing cannot be set up.");
        return;
      }
      this.startPageLoading();
      createInsightClient(item.id)
        .then(result => {
          this.stopPageLoading();
          if (result && result.error) {
            this.setError(result.error);
            return;
          }
          this.loadQueue();
        })
        .catch(() => {
          this.stopPageLoading();
          this.setError("An unexpected error has occurred while linking the client.");
        });
    },
    confirmStampBack(item) {
      this.clearError();
      this.$bvModal
        .msgBoxConfirm("Update CRM with the posted billing figures?", {
          title: "Update CRM",
          okTitle: "Update CRM",
          cancelTitle: "Cancel",
          okVariant: "primary",
          centered: true
        })
        .then(confirmed => {
          if (confirmed) this.runStampBack(item);
        })
        .catch(() => {});
    },
    runStampBack(item) {
      this.startPageLoading();
      stampBack(item.id)
        .then(result => {
          this.stopPageLoading();
          if (result && result.error) {
            this.setError(result.error);
            return;
          }
          if (result && result.exception) {
            this.setError(result.detail || "Updated with exceptions — review the row.");
          }
          this.loadQueue();
        })
        .catch(() => {
          this.stopPageLoading();
          this.setError("An unexpected error has occurred while updating CRM.");
        });
    },
    setError(message) {
      KTUtil.scrollTop();
      this.errorAlert = message;
    },
    clearError() {
      this.errorAlert = null;
    },
    handleError(message) {
      this.stopPageLoading();
      this.setError(message);
    },
    startPageLoading() {
      this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
    },
    stopPageLoading() {
      this.$store.dispatch(REMOVE_BODY_CLASSNAME, "page-loading");
    }
  }
};
</script>

<style lang="scss" scoped>
.page-loading {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
